import React, { useEffect, useState } from "react";
import { Typography, Button } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import ErrorIcon from "@material-ui/icons/Error";
import { Close } from "@material-ui/icons";

const useStyles = () => ({
	container: {
		position: "fixed",
		bottom: 0,
		left: 0,
		right: 0,
		backgroundColor: "#FFFFFF",
		borderRadius: "20px 20px 0 0",
		maxWidth: "480px",
		width: "100%",
		margin: "0 auto",
		zIndex: 1300,
		boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
		animation: "slideUp 0.3s ease-out",
		padding: "20px",
		paddingBottom: "40px",
	},
	closeButton: {
		position: "absolute",
		top: 16,
		right: 16,
		cursor: "pointer",
		zIndex: 1,
	},
	containerChildren: {
		padding: "0 20px 20px 20px",
	},
	content: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		textAlign: "center",
		marginBottom: 24,
	},
	buttonContainer: {
		display: "flex",
		flexDirection: "column",
		gap: 12,
		width: "100%",
	},
	button: {
		width: "100%",
		padding: "14px",
		borderRadius: "12px",
		textTransform: "none",
		fontSize: "16px",
		fontWeight: 600,
	},
	retryButton: {
		backgroundColor: "#25D366",
		color: "#FFFFFF",
		"&:hover": {
			backgroundColor: "#20BA5A",
		},
	},
	closeButtonStyle: {
		backgroundColor: "#F5F5F5",
		color: "#333333",
		"&:hover": {
			backgroundColor: "#E0E0E0",
		},
	},
});

export default function ModalError({
	open,
	onClose,
	message,
	onRetry,
}) {
	const classes = useStyles();
	const { t } = useTranslation();
	const [animationKey, setAnimationKey] = useState(0);

	useEffect(() => {
		if (open) {
			document.body.style.overflow = "hidden";
			setAnimationKey((prev) => prev + 1);
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [open]);

	if (!open) return null;

	const handleBackdropClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<>
			<div
				onClick={handleBackdropClick}
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "rgba(0, 0, 0, 0.5)",
					zIndex: 1299,
					animation: "fadeIn 0.3s ease-out",
				}}
			/>

			<div key={animationKey} style={classes.container}>
				{onClose && (
					<div style={classes.closeButton} onClick={onClose}>
						<Close style={{ fontSize: 20, color: "#666" }} />
					</div>
				)}

				<div style={classes.containerChildren}>
					<div style={classes.content}>
						<ErrorIcon 
							style={{ 
								fontSize: 64, 
								color: "#f44336", 
								marginBottom: 16 
							}} 
						/>
						
						<Typography
							style={{
								fontWeight: 700,
								fontSize: "clamp(24px, 3vw, 30px)",
								textAlign: "center",
								marginBottom: 12,
								color: "#333333",
							}}
						>
							{t("An error occurred")}
						</Typography>

						<Typography
							style={{
								fontSize: "clamp(16px, 2.2vw, 22px)",
								textAlign: "center",
								color: "#666666",
								lineHeight: 1.4,
								marginBottom: 24,
							}}
						>
							{message || t("An unexpected error occurred. Please try again.")}
						</Typography>
					</div>

					<div style={classes.buttonContainer}>
						{onRetry && (
							<Button
								style={{ ...classes.button, ...classes.retryButton }}
								onClick={onRetry}
							>
								{t("Try again")}
							</Button>
						)}
						<Button
							style={{ ...classes.button, ...classes.closeButtonStyle }}
							onClick={onClose}
						>
							{t("Close")}
						</Button>
					</div>
				</div>
			</div>

			<style>
				{`
					@keyframes fadeIn {
						from {
							opacity: 0;
						}
						to {
							opacity: 1;
						}
					}
					@keyframes slideUp {
						from {
							transform: translateY(100%);
						}
						to {
							transform: translateY(0);
						}
					}
				`}
			</style>
		</>
	);
}

